import {
  redrawFragment,
  LitheSpecificElement,
  LitheFC,
} from '@wheelbox/lithejs-engine';
import { goto, withRouteParams } from '@wheelbox/lithejs-router';
import withTranslations, { withCurrentLanguage } from '@wheelbox/lithejs-i18n';
import {
  CheckCircle2Icon,
  CircleIcon,
} from '@wheelbox/lithejs-lucide';

import withPageTitle from '@lib/page';
import {
  fetchTutorialIndex,
  fetchTutorial,
  type TutorialData,
  type TutorialMeta,
} from '@lib/tutorial-loader';
import {
  isSectionCompleted,
  updateLastVisited,
  getProgressPercentage,
} from '@lib/tutorial-progress';
import { updateSlice, withSlice } from '@state/index';

import { APP_TUTORIAL_SLICE } from '@constants/app';

import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarTrigger,
} from '@components/ui';
import TutorialContentView from './content';

import { type TutorialPageSlice } from '@state/index.model';

const TutorialPage: LitheFC = ({
  id = 'tutorial-page',
  testId = 'tutorial-page',
}): LitheSpecificElement => {
  withPageTitle('LitheJS | Tutorial');
  const lng = withCurrentLanguage();
  const t = withTranslations('Tutorial');
  const { section } = withRouteParams();
  const state = withSlice<TutorialPageSlice>(APP_TUTORIAL_SLICE);

  let tutorialData: TutorialData | null = null;
  let isLoading = !!section;

  const loadTutorial = async (filename: string) => {
    isLoading = true;
    redrawFragment(`${id}-tutorial-content`);

    try {
      tutorialData = await fetchTutorial(filename, lng);
      // Track last visited section
      updateLastVisited(filename);
    } catch (error) {
      console.error('Failed to load tutorial:', error);
      tutorialData = null;
    }

    isLoading = false;
    redrawFragment(`${id}-tutorial-content`);
  };

  const getPreviousAndNextDocs = (): {
    previous?: TutorialMeta;
    next?: TutorialMeta;
  } => {
    const sections = state.sections as TutorialMeta[];
    const activeIndex = sections.findIndex((item) => section === item.filename);

    return {
      previous: activeIndex > 0 ? sections[activeIndex - 1] : undefined,
      next:
        activeIndex > -1 && activeIndex < sections.length - 1
          ? sections[activeIndex + 1]
          : undefined,
    };
  };

  const onMountHandler = async () => {
    // Listen for progress changes to update sidebar
    const handleProgressChange = () => {
      redrawFragment(`${id}-sidebar-menu`);
      redrawFragment(`${id}-progress-bar-content`);
    };
    window.addEventListener('tutorial-progress-changed', handleProgressChange);

    if (state.sections.length === 0 || state.language !== lng) {
      try {
        const sections = await fetchTutorialIndex(lng);
        updateSlice<TutorialPageSlice>(APP_TUTORIAL_SLICE, {
          sections,
          language: lng,
        });
        redrawFragment(`${id}-sidebar-menu`);
        redrawFragment(`${id}-progress-bar-content`);
        if (section) {
          redrawFragment(`${id}-breadcrumb-section-title`);
        }
      } catch (error) {
        console.error('Failed to load tutorial index:', error);
      }
    }

    if (section) {
      loadTutorial(section);
    }
  };

  const getSectionTitle = () => {
    const sections = state.sections as TutorialMeta[];
    const sectionData = sections.find((s) => s.filename === section);
    return sectionData?.title || section;
  };

  const handleSectionSelect = (filename: string) => {
    goto(`/tutorial/${filename}`);
  };

  const renderProgressBar = (
    sections: TutorialMeta[]
  ): LitheSpecificElement => {
    const percentage = getProgressPercentage(sections.length);

    return [
      'div',
      { id: `${id}-progress-bar-content`, class: 'px-2 py-2' },
      [
        'div',
        { class: 'flex justify-between text-xs text-muted-foreground mb-1' },
        ['span', {}, t('progress')],
        ['span', {}, `${percentage}%`],
      ],
      [
        'div',
        { class: 'h-1.5 bg-muted rounded-full overflow-hidden' },
        [
          'div',
          {
            class: 'h-full bg-primary transition-all duration-300',
            style: `width: ${percentage}%`,
          },
        ],
      ],
    ];
  };

  const renderSidebarMenu = (): LitheSpecificElement => {
    const sections = state.sections as TutorialMeta[];

    if (sections.length === 0) {
      return [
        'div',
        {
          id: `${id}-sidebar-menu`,
          class: 'p-4 text-muted-foreground text-sm',
        },
        'Loading...',
      ];
    }

    return [
      'div',
      { id: `${id}-sidebar-menu` },
      SidebarMenu(
        {},
        ...sections.map((tutorialSection) => {
          const completed = isSectionCompleted(tutorialSection.filename);
          return SidebarMenuItem(
            {},
            SidebarMenuButton(
              {
                isActive: section === tutorialSection.filename,
                onclick: () => handleSectionSelect(tutorialSection.filename),
              },
              completed
                ? CheckCircle2Icon({
                    size: 16,
                    classes: 'text-green-500 flex-shrink-0',
                  })
                : CircleIcon({
                    size: 16,
                    classes: 'text-muted-foreground flex-shrink-0',
                  }),
              [
                'span',
                { class: completed ? 'text-muted-foreground' : '' },
                tutorialSection.title,
              ]
            )
          );
        })
      ),
    ];
  };

  const renderSidebarProgress = (): LitheSpecificElement => {
    const sections = state.sections as TutorialMeta[];
    if (sections.length === 0) {
      return ['div', { id: `${id}-progress-bar-content` }];
    }
    return renderProgressBar(sections);
  };

  return SidebarProvider(
    {
      id,
      defaultOpen: true,
    },
    [
      'div',
      {
        'data-testid': testId,
        class: 'flex w-full h-[calc(100vh-4rem)]',
        onmount: onMountHandler,
      },
      // Sidebar
      Sidebar(
        {
          side: 'left',
          variant: 'sidebar',
          collapsible: 'offcanvas',
          classes: 'border-r-0',
        },
        SidebarContent(
          { classes: 'pt-4' },
          SidebarGroup(
            {},
            SidebarGroupLabel({}, t('chapters')),
            SidebarGroupContent({}, () => renderSidebarMenu()),
            () => renderSidebarProgress()
          )
        )
      ),

      // Main content area
      SidebarInset(
        { classes: 'flex-1 flex flex-col overflow-hidden' },
        // Header with breadcrumb and trigger
        [
          'header',
          {
            class: 'flex h-14 shrink-0 items-center gap-4 bg-background px-4',
          },
          SidebarTrigger({ classes: 'md:hidden' }),
          Breadcrumb(
            { id: `${id}-breadcrumb` },
            BreadcrumbList(
              { id: `${id}-breadcrumb-list` },
              BreadcrumbItem(
                { id: `${id}-breadcrumb-home` },
                BreadcrumbLink(
                  { id: `${id}-breadcrumb-home-link`, href: '/' },
                  'Home'
                )
              ),
              BreadcrumbSeparator({ id: `${id}-breadcrumb-sep-1` }),
              section
                ? BreadcrumbItem(
                    { id: `${id}-breadcrumb-tutorial` },
                    BreadcrumbLink(
                      {
                        id: `${id}-breadcrumb-tutorial-link`,
                        href: '/tutorial',
                      },
                      'Tutorial'
                    )
                  )
                : BreadcrumbItem(
                    { id: `${id}-breadcrumb-tutorial` },
                    BreadcrumbPage(
                      { id: `${id}-breadcrumb-tutorial-page` },
                      'Tutorial'
                    )
                  ),
              ...(section
                ? [
                    BreadcrumbSeparator({ id: `${id}-breadcrumb-sep-2` }),
                    BreadcrumbItem({ id: `${id}-breadcrumb-section` }, () =>
                      BreadcrumbPage(
                        { id: `${id}-breadcrumb-section-title` },
                        getSectionTitle()
                      )
                    ),
                  ]
                : [])
            )
          ),
        ],

        // Tutorial content
        [
          'main',
          { class: 'flex-1 overflow-auto p-6' },
          () =>
            TutorialContentView({
              id: `${id}-tutorial-content`,
              data: tutorialData,
              sectionId: section,
              isLoading,
              ...getPreviousAndNextDocs(),
              onselect: handleSectionSelect,
            }),
        ]
      ),
    ]
  );
};

export default TutorialPage;
